import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Building2, MapPin, Clock, Award } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import RelatedCaseStudies from '@/components/RelatedCaseStudies';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const CommercialEPCDorset = () => {
  const faqs = [
    {
      question: "How much does a Commercial EPC (NDEPC) cost in Dorset?",
      answer: "Level 3 Commercial EPC pricing in Dorset varies by property type and size. Small offices/retail (up to 500 m²) typically cost £180–£400. Medium warehouses (500–2,000 m²) range from £400–£800. Larger properties £800–£1,500+. A transparent travel surcharge may apply for properties in Dorset — calculated and shown before you pay."
    },
    {
      question: "Do you cover all Dorset areas for NDEPC and NDEA services?",
      answer: "Yes. We cover all of Dorset including Dorchester, Weymouth, Portland, Poole, Bournemouth, Christchurch, Ferndown, Wimborne, Blandford Forum, Shaftesbury, Sherborne, Bridport, Lyme Regis, Swanage and the Purbeck area across East Dorset, North Dorset, West Dorset and BCP."
    },
    {
      question: "What is a Level 3 Commercial EPC and will my Dorset property qualify?",
      answer: "A Level 3 NDEPC covers offices, shops, warehouses, light industrial units, pubs, cafés, hotels, B&Bs, guesthouses, surgeries and most Dorset commercial premises. It is the most common non-domestic assessment and keeps the modelling proportionate while producing a fully lodged, legally compliant certificate."
    },
    {
      question: "What types of Dorset commercial property do you assess?",
      answer: "We assess all non-domestic buildings in Dorset — offices, retail units, warehouses, industrial premises, hotels, B&Bs, guesthouses, restaurants, pubs, marine facilities, care homes, schools, leisure centres and mixed-use properties across Dorset's coastal resorts and inland market towns."
    },
    {
      question: "Do you also do SBEM calculations and new-build EPCs in Dorset?",
      answer: "Yes. Alongside the core NDEPC we provide full SBEM calculations for new-build and major refurbishment projects needing Part L Building Regulations compliance, plus the as-built commercial EPC for Building Control sign-off."
    },
    {
      question: "My Dorset building is rated F or G — what now?",
      answer: "Under MEES (April 2023) it is unlawful to continue to let a Dorset commercial property rated F or G without a registered exemption. Under DESNZ's June 2026 interim response, the 2027 EPC C milestone was dropped; a proposed EPC B by 2031 will apply only to buildings over 1,000 m² (pending secondary legislation), and smaller buildings remain at Band E. Our Commercial Energy Advice (MEES consultancy) report sits alongside the EPC and models realistic upgrades with cost and payback — particularly useful for Dorset hospitality buildings on the Jurassic Coast."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Commercial EPC Dorset | NDEPC, NDEA, Level 3 | Dorchester, Weymouth, Poole, Bournemouth | Energy Performance Direct</title>
        <meta name="description" content="Accredited NDEA Commercial EPC (NDEPC) assessments across Dorset from £180. Level 3 EPCs and SBEM calculations. Bournemouth, Poole, Dorchester, Weymouth — MEES advice included. Call 01392 980077." />
        <meta name="keywords" content="Commercial EPC Dorset, NDEPC Dorset, Non-Domestic Energy Performance Certificate Dorset, NDEA Dorset, Level 3 EPC Dorset, Commercial EPC Bournemouth, Commercial EPC Poole, Commercial EPC Dorchester, SBEM Dorset, MEES commercial Dorset" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/commercial-epc-dorset" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Commercial Energy Performance Certificate (NDEPC)",
            "name": "Commercial EPC Dorset",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Energy Performance Direct",
              "telephone": "+44-1392-980077",
              "email": "info@energyperformancedirect.co.uk",
              "priceRange": "£180 - £1500"
            },
            "areaServed": { "@type": "AdministrativeArea", "name": "Dorset" }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(f => ({
              "@type": "Question",
              "name": f.question,
              "acceptedAnswer": { "@type": "Answer", "text": f.answer }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <section className="py-16 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">NDEA Accredited · Covering All Dorset</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Commercial EPC (NDEPC) Assessments in Dorset
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                Accredited Non-Domestic Energy Assessors (NDEA) for Dorset businesses. Level 3 Commercial EPCs, SBEM calculations and MEES advice — Bournemouth, Poole, Dorchester, Weymouth and every Dorset town.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/booking" data-testid="dorset-book-btn">
                  <Button className="text-white hover:opacity-90 px-8 py-6 text-lg rounded-full shadow-lg" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                    Get a Quote in 2 Minutes
                  </Button>
                </Link>
                <a href="tel:01392980077" data-testid="dorset-call-btn">
                  <Button variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Award className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">NDEA Qualified</h3>
                  <p className="text-sm text-gray-600">Elmhurst accredited assessors</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Clock className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">5–7 Day Delivery</h3>
                  <p className="text-sm text-gray-600">Fast NDEPC turnaround</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <MapPin className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">County Coverage</h3>
                  <p className="text-sm text-gray-600">All Dorset districts</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Building2 className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">From £180</h3>
                  <p className="text-sm text-gray-600">Level 3 pricing</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Commercial EPC Services Across Dorset</h2>

              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                <p>
                  Energy Performance Direct provides professional Non-Domestic Energy Performance Certificate (NDEPC) assessments for commercial buildings throughout Dorset. Operating across the West Country since 2009, our NDEA assessors have extensive experience surveying Dorset&rsquo;s diverse commercial property stock &mdash; coastal hotels and guesthouses in Weymouth, Portland and Bournemouth, retail units in Dorchester, industrial premises in Ferndown and Wimborne, marine facilities in Poole Harbour, and business premises across the Jurassic Coast.
                </p>

                <p>
                  A <Link to="/commercial-epc" className="text-green-600 hover:text-green-700 underline">Commercial EPC (NDEPC)</Link> is legally required whenever you sell, rent or construct a commercial building in Dorset. Most Dorset properties fall within the Level 3 category &mdash; offices, shops, small industrial units, pubs, cafés, hotels, B&amp;Bs and surgeries &mdash; which is our everyday specialism. We provide assessments across Dorchester, Weymouth, Portland, Poole, Bournemouth, Christchurch, Ferndown, Wimborne, Blandford Forum, Shaftesbury, Sherborne, Bridport, Lyme Regis, Swanage and the Purbeck area.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">SBEM &amp; new-build EPCs across Dorset</h3>
                <p>
                  In addition to standard NDEPCs we produce full SBEM calculations for new-build and refurbishment projects needing Part L Building Regulations compliance, plus the as-built commercial EPC for Building Control sign-off. See our <Link to="/sbem-explained" className="text-green-600 hover:text-green-700 underline">SBEM explained guide</Link>.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">MEES consultancy for Dorset landlords</h3>
                <p>
                  Since April 2023 it is unlawful to continue to let a Dorset commercial property rated F or G without a registered exemption. Under DESNZ&rsquo;s June 2026 interim response, the 2027 EPC C milestone was dropped; a proposed EPC B by 2031 will apply only to buildings over 1,000 m² (pending secondary legislation), and smaller Dorset buildings continue at Band E. Our <Link to="/services/commercial_advice" className="text-green-600 hover:text-green-700 underline">Commercial Energy Advice (MEES) consultancy</Link> sits alongside the EPC and models realistic upgrades (LED re-lamping, controls, insulation, heat pump retrofit, solar PV) with cost and payback &mdash; particularly valuable for older Bournemouth/Poole period offices and coastal hotels.
                </p>

                <p>
                  Commercial EPC pricing in Dorset: Small offices/retail (up to 500 m²) £180–£400, medium warehouses (500–2,000 m²) £400–£800, large properties £800–£1,500+. Contact us for a Dorset-specific quote.
                </p>
              </div>

              <div className="mt-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Dorset Commercial EPC FAQs</h2>
                <div className="space-y-6">
                  {faqs.map((faq, idx) => (
                    <Card key={idx} className="border-none shadow-md">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                        <p className="text-gray-700">{faq.answer}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="mt-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Industrial &amp; Warehouse EPCs in Dorset</h2>
                <p className="text-gray-700 mb-6">
                  Need a specialist industrial or warehouse EPC in Dorset? Our assessors cover Nuffield, Ferndown, Fleets and all major Dorset industrial estates.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Link to="/industrial-epc-dorset" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <h3 className="font-semibold mb-1">Industrial EPCs Dorset</h3>
                    <p className="text-sm text-gray-600">Poole, Bournemouth, Ferndown industrial areas</p>
                  </Link>
                  <Link to="/warehouse-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <h3 className="font-semibold mb-1">Warehouse EPCs</h3>
                    <p className="text-sm text-gray-600">Distribution centres and logistics facilities</p>
                  </Link>
                  <Link to="/commercial-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <h3 className="font-semibold mb-1">All Commercial EPC Services</h3>
                    <p className="text-sm text-gray-600">NDEPC, SBEM &amp; MEES advice</p>
                  </Link>
                </div>
              </div>

              <div className="mt-16 rounded-2xl p-8 text-white text-center" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                <h2 className="text-3xl font-bold mb-4">Book Your Dorset Commercial EPC</h2>
                <p className="text-lg mb-6">Professional NDEPCs across Dorset. Call 01392 980077 or book online.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/booking">
                    <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">Book Online Now</Button>
                  </Link>
                  <a href="tel:01392980077">
                    <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg rounded-full">
                      <Phone className="w-5 h-5 mr-2" />Call 01392 980077
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <RelatedCaseStudies filterType="commercial" title="Dorset commercial buildings — see it in practice" subtitle="See how the same NDEA service has worked for other commercial buildings in the South West." limit={3} />

        <Footer />
      </div>
    </>
  );
};

export default CommercialEPCDorset;
